import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { esvTipDoc, trimNum } from '@/shared/taxTips';
import InfoQ from '@/shared/components/InfoQ';
import {
  vacationOrder,
  vacationCalculation,
  type VacationType,
  type VacationCalculationPreview,
} from '@/api/vacation';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { useConfirm } from '@/components/ConfirmProvider';

const now = new Date();
const fmt2 = (v: string | number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABELS: Record<VacationType, string> = {
  ANNUAL_BASIC: 'Щорічна основна',
  ANNUAL_ADDITIONAL: 'Щорічна додаткова',
  SOCIAL: 'Соціальна',
  UNPAID: 'Без збереження зарплати',
  CREATIVE: 'Творча',
};
const TYPE_ORDER: VacationType[] = ['ANNUAL_BASIC', 'ANNUAL_ADDITIONAL', 'UNPAID'];

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  vacation_type: VacationType;
  vacation_start_date: string;
  vacation_end_date: string;
  reason: string;
  comment: string;
}

const todayISO = now.toISOString().slice(0, 10);
const EMPTY: FormState = {
  organization_id: '', document_number: '', document_date: now.toISOString().slice(0, 16),
  employee_id: '', vacation_type: 'ANNUAL_BASIC',
  vacation_start_date: todayISO, vacation_end_date: todayISO, reason: '', comment: '',
};

function daysBetween(a: string, b: string): number {
  if (!a || !b) return 0;
  const d = (new Date(b).getTime() - new Date(a).getTime()) / 86400000;
  return d >= 0 ? Math.floor(d) + 1 : 0;
}

export default function VacationOrderEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('documents.vacation_order.create');

  const docQuery = useQuery({
    queryKey: ['vacation-order', id],
    queryFn: () => vacationOrder.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [preview, setPreview] = useState<VacationCalculationPreview | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [posted, setPosted] = useState(false);
  // Незбережені правки наказа → деактивують «Розрахувати» (preview застарів).
  const [dirty, setDirty] = useState(false);
  // Id вже створеного нарахування цього наказа (щоб ОНОВЛЮВАТИ, а не дублювати).
  const [existingCalcId, setExistingCalcId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Ручний override середньоденної (бухгалтер перебив у табличній частині).
  const [avgOverride, setAvgOverride] = useState('');

  const empQ = useQuery({
    queryKey: ['employees', form.organization_id],
    queryFn: () => refs.employees(form.organization_id, { active_only: true }),
    enabled: !!form.organization_id,
  });
  // V3: залишок днів відпустки для м'якого банера.
  const vacBalanceQ = useQuery({
    queryKey: ['vac-balance', form.employee_id, form.organization_id],
    queryFn: () => vacationOrder.employeeBalance(form.employee_id, form.organization_id),
    enabled: !!form.employee_id && !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      // Авто-вибір org: спершу з availableOrgs (по праву create), fallback — повний
      // список (OWNER/повільний /auth/me). Без fallback organization_id лишався порожнім
      // → save падав «Body missing organization_id».
      const firstOrg = availableOrgs[0]?.id ?? orgList.data?.items[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id, document_number: d.document_number,
        document_date: d.document_date.slice(0, 16), employee_id: d.employee_id,
        vacation_type: d.vacation_type, vacation_start_date: d.vacation_start_date,
        vacation_end_date: d.vacation_end_date, reason: d.reason ?? '', comment: d.comment ?? '',
      });
      setSavedId(d.id);
      setPosted(d.posted);
      setDirty(false);
      if (d.vacation_calculation_id) {
        setExistingCalcId(d.vacation_calculation_id);
        vacationCalculation.get(d.vacation_calculation_id).then((c) => setPreview(c as any)).catch(() => {});
      }
    }
  }, [isNew, docQuery.data, availableOrgs, orgList.data]);

  const days = daysBetween(form.vacation_start_date, form.vacation_end_date);
  const isUnpaid = form.vacation_type === 'UNPAID';

  function buildCreate() {
    return {
      organization_id: form.organization_id,
      document_number: form.document_number || undefined,
      document_date: form.document_date, employee_id: form.employee_id,
      vacation_type: form.vacation_type,
      vacation_start_date: form.vacation_start_date,
      vacation_end_date: form.vacation_end_date,
      days_count: days,
      reason: form.reason || null,
      is_paid: !isUnpaid,
      comment: form.comment || null,
    };
  }
  // PATCH наказа приймає ЛИШЕ ці поля (VacationOrderUpdate, extra=forbid).
  function buildUpdate() {
    return {
      document_date: form.document_date,
      vacation_start_date: form.vacation_start_date,
      vacation_end_date: form.vacation_end_date,
      days_count: days,
      reason: form.reason || null,
      comment: form.comment || null,
    };
  }

  // Зберегти наказ (DRAFT) — як у наказі звільнення.
  const create = useMutation({
    mutationFn: () => vacationOrder.create(buildCreate()),
    onSuccess: (doc) => {
      setSavedId(doc.id); setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
      navigate(`/hr/vacation-orders/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });
  // Зберегти і провести наказ.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await vacationOrder.create(buildCreate());
      try { await vacationOrder.post(doc.id); } catch { /* вже проведено */ }
      return doc;
    },
    onSuccess: (doc) => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
      navigate(`/hr/vacation-orders/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });
  // Провести вже збережений наказ.
  const postOrder = useMutation({
    mutationFn: () => vacationOrder.post(savedId!),
    onSuccess: () => {
      setPosted(true); setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });
  // Зберегти зміни вже збереженого наказа (PATCH) → дозволяє Розрахувати знову.
  const updateOrder = useMutation({
    mutationFn: () => vacationOrder.update(savedId!, buildUpdate()),
    onSuccess: () => {
      setError(null); setDirty(false); setPreview(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
      qc.invalidateQueries({ queryKey: ['vacation-order', savedId] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });
  // Розпровести наказ.
  const unpostOrder = useMutation({
    mutationFn: () => vacationOrder.unpost(savedId!),
    onSuccess: () => {
      setPosted(false); setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розпроведення'),
  });
  // Видалити наказ РАЗОМ зі зв'язаним нарахуванням відпускних.
  const removeOrder = useMutation({
    mutationFn: () => vacationOrder.remove(savedId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
      qc.invalidateQueries({ queryKey: ['vacation-calculations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate('/hr-orders');
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка видалення'),
  });

  // Розрахувати — ТIЛЬКИ preview (наказ уже збережено). НЕ створює нарахування.
  const calc = useMutation({
    mutationFn: () => vacationCalculation.preview({
      organization_id: form.organization_id,
      vacation_order_id: savedId!,
      average_daily_override: avgOverride ? avgOverride : null,
    }),
    onSuccess: (r) => {
      setPreview(r); setError(null);
      if (!avgOverride) setAvgOverride(trimNum(r.average_daily_wage));
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розрахунку'),
  });

  // Зберегти нарахування. Якщо нарахування цього наказа ВЖЕ Є — ОНОВИТИ його
  // (PATCH середньої), інакше створити чернетку (createDraft). Не дублюємо (уник 409).
  const saveCalc = useMutation({
    mutationFn: () => {
      const avg = avgOverride ? avgOverride : '';
      if (existingCalcId) {
        return vacationCalculation.update(existingCalcId, avg);
      }
      return vacationCalculation.createDraft({
        organization_id: form.organization_id,
        vacation_order_id: savedId!,
        average_daily_override: avgOverride ? avgOverride : null,
      });
    },
    onSuccess: (doc) => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-calculations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate(`/hr/vacation-calculations/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження нарахування'),
  });

  const canSaveOrder = !!form.organization_id && !!form.employee_id && days > 0;
  // Розрахувати — лише для збереженого наказа БЕЗ незбережених правок (dirty).
  const canCalc = !!savedId && !isUnpaid && !posted && !dirty;

  function setF(patch: Partial<FormState>) {
    setForm((f) => ({ ...f, ...patch }));
    if (!posted) { setPreview(null); setDirty(true); }
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew && !savedId ? 'Новий наказ (відпустка)' : `Наказ (відпустка) № ${form.document_number}`}
          {posted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/hr-orders')}
          onSave={() => isNew ? create.mutate() : updateOrder.mutate()}
          saveDisabled={(isNew && !canSaveOrder) || create.isPending || updateOrder.isPending}
          isPosted={posted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : postOrder.mutate()}
          postDisabled={isNew && !canSaveOrder}
          postPending={createAndPost.isPending || postOrder.isPending}
          postTitle="Зберігає та проводить наказ"
          onUnpost={() => unpostOrder.mutate()}
          unpostPending={unpostOrder.isPending}
          onDelete={() => {
            confirm('Видалити наказ РАЗОМ зі зв\'язаним нарахуванням відпускних?')
              .then((ok) => { if (ok) removeOrder.mutate(); });
          }}
          deletePending={removeOrder.isPending}
          deleteTitle="Видаляє наказ разом зі зв'язаним нарахуванням відпускних"
          onPrint={() => navigate(`/hr/vacation-orders/${savedId}/print`)}
        />
      </div>

      {posted && (
        <div style={{ background: '#dcfce7', color: '#166534', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>✓ Наказ проведено.</div>
      )}
      {error && (
        <div style={{ background: '#fee', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 200 }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew || !!savedId}
            onChange={(e) => setF({ organization_id: e.target.value, employee_id: '' })}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 240 }}>
          <label>Працівник *</label>
          <select value={form.employee_id} disabled={posted}
            onChange={(e) => setF({ employee_id: e.target.value })}>
            <option value="">—</option>
            {empQ.data?.map((e) => <option key={e.id} value={e.id}>{e.code ? `${e.code} — ` : ''}{e.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 160 }}>
          <label>Дата документа</label>
          <DatePopover value={form.document_date.slice(0, 10)} disabled={posted}
            onChange={(v) => setF({ document_date: `${v}T00:00` })} />
        </div>
      </div>

      <div className="field" style={{ maxWidth: 360, marginTop: '0.75rem' }}>
        <label>Вид відпустки *</label>
        <select value={form.vacation_type} disabled={posted}
          onChange={(e) => setF({ vacation_type: e.target.value as VacationType })}>
          {TYPE_ORDER.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
        {isUnpaid && (
          <small style={{ color: 'var(--muted)' }}>Без збереження зарплати — розрахунок не робиться.</small>
        )}
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.5rem' }}>
        <div className="field" style={{ minWidth: 150 }}>
          <label>Дата початку *</label>
          <DatePopover value={form.vacation_start_date} disabled={posted} onChange={(v) => setF({ vacation_start_date: v })} />
        </div>
        <div className="field" style={{ minWidth: 150 }}>
          <label>Дата закінчення *</label>
          <DatePopover value={form.vacation_end_date} disabled={posted} onChange={(v) => setF({ vacation_end_date: v })} />
        </div>
        <div className="field" style={{ width: 90 }}>
          <label>Днів</label>
          <input value={days} readOnly disabled style={{ textAlign: 'center', background: '#f1f5f9' }} />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label>Підстава</label>
          <input value={form.reason} disabled={posted} placeholder="заява працівника…"
            onChange={(e) => setF({ reason: e.target.value })} />
        </div>
      </div>

      {/* V3: м'який банер залишку днів (тільки щорічні відпустки). */}
      {form.employee_id && vacBalanceQ.data &&
        (form.vacation_type === 'ANNUAL_BASIC' || form.vacation_type === 'ANNUAL_ADDITIONAL') && (() => {
        const bal = Number(form.vacation_type === 'ANNUAL_BASIC'
          ? vacBalanceQ.data.annual_basic_balance
          : vacBalanceQ.data.annual_additional_balance);
        const notEnough = days > bal;
        return (
          <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.75rem', borderRadius: 6,
            background: notEnough ? '#fef3c7' : '#ecfdf5',
            border: `1px solid ${notEnough ? '#f59e0b' : '#a7f3d0'}`, fontSize: '0.85rem' }}>
            Залишок днів цього виду відпустки: <strong>{bal.toFixed(2)}</strong>
            {notEnough && (
              <span style={{ color: '#b45309' }}>
                {' '}⚠ Запитується {days} дн. — більше за залишок. Дозволено (авансова
                відпустка), але перевірте.
              </span>
            )}
          </div>
        );
      })()}

      {/* Розрахунок відпускних — кнопка активна ЛИШЕ після збереження наказа
          (як у наказі звільнення). Розрахувати = preview, НЕ створює нарахування. */}
      {!posted && !isUnpaid && (
        <div style={{ marginTop: '0.9rem', borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
            <div className="field" style={{ width: 180 }}>
              <label>Середньоденна {preview?.average_from_import ? '⚠' : ''}</label>
              <input type="number" step="0.01" value={avgOverride} disabled={!savedId}
                placeholder="авто з заробітку"
                onChange={(e) => { setAvgOverride(e.target.value); setPreview(null); }} />
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <button className="primary" disabled={!canCalc || calc.isPending}
                onClick={() => calc.mutate()}
                title={!savedId
                  ? 'Спершу збережіть або проведіть наказ — тоді можна розрахувати відпускні'
                  : dirty
                    ? 'Спершу збережіть зміни наказа («Зберегти») — тоді розрахунок оновиться'
                    : 'Перерахунок брутто/ПДФО/ВЗ/ЄСВ за дні відпустки × середню.'}>
                Розрахувати
              </button>
            </div>
          </div>
          {!savedId && (
            <p style={{ color: '#64748b', fontSize: '0.8rem', margin: 0 }}>
              Кнопка «Розрахувати» стане активною після збереження або проведення наказа.
            </p>
          )}
          {savedId && dirty && (
            <p style={{ color: '#b45309', fontSize: '0.8rem', margin: 0 }}>
              Ви змінили дані наказа. Натисніть «Зберегти» — після цього можна
              перерахувати відпускні.
            </p>
          )}
        </div>
      )}

      {preview && (
        <div style={{ marginTop: '0.5rem' }}>
          {/* Таблична частина — дизайн як у нарахуванні ЗП/компенсації. */}
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'right' }}>
                <th style={{ textAlign: 'left', padding: '0.2rem 0.3rem' }}>Працівник</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 150 }}>Період розрахунку</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>Заробіток 12 міс</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>
                  Середня{preview.average_from_import ? ' ⚠' : ''}
                </th>
                <th style={{ padding: '0.2rem 0.3rem', width: 60 }}>Днів</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>Брутто</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ПДФО</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ВЗ</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ЄСВ</th>
                <th style={{ padding: '0.2rem 0.3rem', width: 100 }}>До виплати</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.15rem 0.3rem' }}>
                  {empQ.data?.find((e) => e.id === form.employee_id)?.name ?? '—'}
                </td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{preview.calc_period_start} — {preview.calc_period_end}</td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{fmt2(preview.total_earnings_12m)}</td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{fmt2(preview.average_daily_wage)}</td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{preview.days_count}</td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right', fontWeight: 700 }}>{fmt2(preview.vacation_gross_amount)}</td>
                <td style={{ padding: '0.15rem 0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                      value={trimNum(preview.pdfo_amount)}
                      style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                    <InfoQ title={`ПДФО — ${trimNum(preview.pdfo_rate)}% від брутто.`} />
                  </div>
                </td>
                <td style={{ padding: '0.15rem 0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                      value={trimNum(preview.wz_amount)}
                      style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                    <InfoQ title={`Військовий збір — ${trimNum(preview.wz_rate)}% від брутто.`} />
                  </div>
                </td>
                <td style={{ padding: '0.15rem 0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                      value={trimNum(preview.esv_amount)}
                      style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                    <InfoQ title={esvTipDoc(preview.esv_amount, preview.esv_rate)} />
                  </div>
                </td>
                <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right', fontWeight: 700 }}>{fmt2(preview.net_amount)}</td>
              </tr>
            </tbody>
          </table>
          </div>

          {/* Зберегти нарахування — створює чернетку розрахунку (як у наказі звільнення). */}
          {!posted && savedId && (
            <button className="primary" style={{ marginTop: '0.5rem' }}
              disabled={saveCalc.isPending}
              onClick={() => saveCalc.mutate()}>
              💾 Зберегти нарахування
            </button>
          )}

          {preview.average_from_import && (
            <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
              background: '#fef3c7', border: '1px solid #f59e0b', fontSize: '0.8rem',
              color: '#b45309' }}>
              ⚠ Середня врахувала імпортовані з 1С нарахування — перевірте суму.
            </div>
          )}

          {(preview.monthly_parts?.length ?? 0) > 1 && (
            <div style={{ marginTop: '0.75rem' }}>
              <h4 style={{ margin: '0 0 0.3rem', fontSize: '0.85rem', color: '#b45309' }}>
                По місяцях (перехідна відпустка — для звітності)
              </h4>
              <table style={{ fontSize: '0.8rem', borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'right' }}>
                    <th style={{ textAlign: 'left', padding: '0.2rem 0.4rem' }}>Місяць</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>Днів</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>Брутто</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>ПДФО</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>ВЗ</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>ЄСВ</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.monthly_parts!.map((m) => (
                    <tr key={`${m.period_year}-${m.period_month}`} style={{ textAlign: 'right', borderTop: '1px solid #eee' }}>
                      <td style={{ textAlign: 'left', padding: '0.15rem 0.4rem' }}>
                        {m.period_year}-{String(m.period_month).padStart(2, '0')}
                      </td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{m.days_in_month}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{fmt2(m.gross_amount)}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{fmt2(m.pdfo_amount)}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{fmt2(m.wz_amount)}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{fmt2(m.esv_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

